// Across Indonesia — Editorial blog posts for international travelers
// 36 hand-curated posts across destinations, tour packages, culture, food, tips, adventures.

const slugify = (s) =>
  s.toLowerCase().trim()
    .replace(/['']/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const make = (
  title,
  category,
  excerpt,
  body,
  image,
  author = "Across Indonesia Editorial",
  readTime = "6 min read",
  date
) => ({
  slug: slugify(title),
  title,
  category,
  excerpt,
  body,
  image,
  author,
  readTime,
  date,
});

export const CATEGORIES = [
  "All",
  "Destinations",
  "Tour Packages",
  "Culture",
  "Food",
  "Adventure",
  "Travel Tips",
];

export const BLOG_POSTS = [
  // ──────────────────────── DESTINATIONS (10) ────────────────────────
  make(
    "Bali Beyond the Beaches: A Quiet Guide to the Island's Inland Heart",
    "Destinations",
    "Ubud's terraces at sunrise, Sidemen's loom-shacks, Munduk's coffee farms — the Bali international travelers rarely meet.",
    "Most first-time visitors picture Bali as a stretch of southern beach. The Bali we love starts further inland — in the cool, mist-folded valleys of Munduk, in the rice terraces of Tegallalang before the tour buses arrive, in the silver-smiths' courtyards of Celuk and the weaving shacks of Sidemen. This guide walks you through a five-day itinerary that follows the island's inland spine: a private temple ceremony at dawn, a slow lunch with a Brahmin family, a coffee-farm walk through clove forests, and a Balinese cooking class in a 1920s riverside compound. We pair each day with the right driver, the right time of day, and the right level of intimacy — because Bali is not a destination, it is a relationship.",
    "https://images.pexels.com/photos/35428411/pexels-photo-35428411.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1600",
    "Made Wirawan",
    "8 min read",
    "2026-01-12"
  ),
  make(
    "Komodo National Park: How to Visit the Dragons Responsibly",
    "Destinations",
    "Beyond the postcard pink beach — a respectful, low-impact way to meet the world's largest lizard and the manta highways below.",
    "Komodo is one of the great wildlife encounters left on the planet — and one of the easiest to do badly. This is our complete briefing for international travelers: when to go (April, May, late October), why we always recommend a 3-night liveaboard over a day-boat from Labuan Bajo, which islands are open to walking (Rinca is quieter than Komodo Island itself), and how the local rangers prefer you behave on the trails. We also cover the marine half: the manta cleaning stations at Manta Point, the current-swept reef at Batu Bolong, and the village of Komodo where rattan baskets and dragon carvings still come from a single family of weavers.",
    "https://images.unsplash.com/photo-1604560929658-bbc3c2ba6a36?auto=format&fit=crop&w=1600&q=80",
    "Captain Reza Pratama",
    "9 min read",
    "2026-01-08"
  ),
  make(
    "Raja Ampat: Why the Four Kings Are Worth the Long Flight",
    "Destinations",
    "Karst islets, coral wallpaper, and the Misool homestays redefining sustainable luxury in West Papua.",
    "Raja Ampat — the 'Four Kings' — sits at the bull's-eye of the Coral Triangle. More marine species live in a single bay here than in the entire Caribbean. We explain the long journey honestly (Jakarta → Sorong → speedboat → resort), which months reward the effort (October to April for the calmest seas, July for whale sharks at Cendrawasih Bay), and the difference between the three main hubs: Waigeo for ease, Misool for solitude, and the liveaboard route for everything in between. Includes our short-list of conservation-led resorts and homestays we book repeat travelers into.",
    "https://images.pexels.com/photos/10482395/pexels-photo-10482395.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1600",
    "Yosef Manuhutu",
    "10 min read",
    "2026-01-04"
  ),
  make(
    "Yogyakarta: The Cultural Capital You Cannot Skip",
    "Destinations",
    "Borobudur at dawn, Prambanan at dusk, batik ateliers in between — and the silver workshops of Kotagede few tourists find.",
    "Yogyakarta is the still-beating cultural heart of Java. Before the day-trippers arrive at Borobudur, our guides take small groups onto the eastern stupa terrace at 4:30 a.m. — coffee, fog, and the world's largest Buddhist monument emerging out of the volcanoes. We pair Borobudur with a midday silversmith demonstration in Kotagede, a private gamelan session inside the kraton walls, and a sunset at the Hindu temple complex of Prambanan. This guide is your map to a three-day Yogya that feels like a private museum visit.",
    "https://images.pexels.com/photos/19758828/pexels-photo-19758828.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1600",
    "Siti Rahmawati",
    "7 min read",
    "2026-01-02"
  ),
  make(
    "Sumba Island: The Wild, Generous Frontier of Eastern Indonesia",
    "Destinations",
    "Stone-aged megaliths, Pasola spear-riders, and the empty surf coast of Nihiwatu — Sumba is the next great Indonesian destination.",
    "If Bali is a relationship, Sumba is a first letter. The island still keeps the Marapu animist religion alive in stone-grave villages where every family has a megalith older than the Renaissance. Each February, the Pasola festival sees mounted spear-riders charge each other across a beach — an event international visitors are still rare at, and we can place you in a host family for. We balance this cultural immersion with the surf coast of Nihiwatu, where one of the world's best beaches meets one of the world's most considered eco-resorts.",
    "https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?auto=format&fit=crop&w=1600&q=80",
    "Umbu Hapu",
    "8 min read",
    "2025-12-28"
  ),
  make(
    "Flores: The Slow Drive From Labuan Bajo to Maumere",
    "Destinations",
    "Spider-web rice fields, three-coloured volcanic lakes, ikat-weaving villages — the seven-day overland route most travelers miss.",
    "We are quietly obsessed with the overland route across Flores. Eight hundred kilometers of mountain road that pass through Wae Rebo (a UNESCO-recognised conical-roof village reachable only by hike), the spider-web rice fields of Cancar, the ikat-weaving women of Bena, and the surreal tri-coloured crater lakes of Kelimutu at dawn. This post details our seven-day itinerary, the driver we always send, the homestays we love, and the precise reason this is the trip our most-traveled clients call back about the most.",
    "https://images.unsplash.com/photo-1588842367425-8270e4b3002c?auto=format&fit=crop&w=1600&q=80",
    "Boni Wangge",
    "11 min read",
    "2025-12-22"
  ),
  make(
    "Lombok and the Gilis: Bali's Quieter Neighbour",
    "Destinations",
    "Mount Rinjani's summit, the Sasak villages of the south, and the three Gili islands at the speed each rewards.",
    "Lombok sits an hour's ferry east of Bali — and a generation behind in tourist traffic. We help international travelers decide between the three Gili islets (Trawangan for nightlife, Air for couples, Meno for honeymooners), and pair them with a slow inland route through Tetebatu, the Sasak weaving village of Sade, and — for the brave — a guided two-day Rinjani volcano ascent. Includes a frank breakdown of physical difficulty and the seasons that are safe to climb.",
    "https://images.unsplash.com/photo-1604560929658-bbc3c2ba6a36?auto=format&fit=crop&w=1600&q=80",
    "Lalu Hamzah",
    "8 min read",
    "2025-12-18"
  ),
  make(
    "Sulawesi's Toraja Highlands: A Culture That Treats Death as a Festival",
    "Destinations",
    "Cliff-carved tombs, eight-day funerals, and the bamboo-roofed tongkonan houses of central Sulawesi.",
    "In the Toraja highlands of central Sulawesi, funeral ceremonies are the most important social event of the year — week-long celebrations involving buffalo sacrifice, traditional dance, and entire villages gathering. For respectful international travelers, witnessing a Rambu Solo' funeral is one of the most moving experiences in Southeast Asia. We outline the strict etiquette, what to wear, what to bring, and how our local hosts walk you through the ceremony's nine distinct stages without ever making you feel like an intruder.",
    "https://images.unsplash.com/photo-1675206362603-b3c3c3ca47c6?auto=format&fit=crop&w=1600&q=80",
    "Rante Pongbatu",
    "10 min read",
    "2025-12-14"
  ),
  make(
    "Java's Volcano Highway: Bromo, Ijen and the Sea of Sand",
    "Destinations",
    "Three sunrises, two volcanoes, and the cobalt sulfur flames most travelers only see in photographs.",
    "Mount Bromo's caldera at sunrise is the postcard. The hike down its sand sea on horseback, the dawn breakfast on the ridge of the parent volcano Penanjakan, and the late-night ascent of Mount Ijen to see its electric-blue sulfur flames — these are the parts of the Java volcano route most international tour operators don't sequence properly. This three-day briefing tells you the exact order, the gear list, and why we always pair this route with a slow recovery night in the colonial hill town of Malang.",
    "https://images.unsplash.com/photo-1518043610038-064362b44076?auto=format&fit=crop&w=1600&q=80",
    "Bagus Pratama",
    "9 min read",
    "2025-12-10"
  ),
  make(
    "The Banda Islands: Where Nutmeg Once Cost More Than Gold",
    "Destinations",
    "Spice-trade ruins, untouched coral, and a Dutch-era guesthouse in the middle of the Banda Sea.",
    "The Banda Islands were once the only place on Earth where nutmeg grew. The Dutch and English fought naval wars over them. Today they are one of the great forgotten corners of Indonesia: a cluster of ten tiny volcanic islands in the middle of the Banda Sea, reachable by a weekly Pelni ferry or a charter flight via Ambon. Spice plantations still operate. The 17th-century Dutch governor's mansion still stands. The coral wall ten metres off the main harbour is one of the healthiest in the world. This is our complete logistical guide.",
    "https://images.unsplash.com/photo-1583212292454-1fe6229603b7?auto=format&fit=crop&w=1600&q=80",
    "Des Alwi Heritage Team",
    "9 min read",
    "2025-12-05"
  ),

  // ──────────────────────── TOUR PACKAGES (8) ────────────────────────
  make(
    "Inside Our Signature 14-Day 'Across Indonesia' Itinerary",
    "Tour Packages",
    "Bali → Yogyakarta → Komodo → Raja Ampat in two carefully paced weeks of private guides and curated stays.",
    "Our signature itinerary covers four flagship regions in fourteen days without the bus-tour pace. Day-by-day: three nights in Ubud (private temple ceremony, rice-terrace breakfast, traditional cooking class), three in Yogyakarta (dawn Borobudur, Prambanan dusk, silversmith workshop), three on a Komodo liveaboard (Padar, Manta Point, Pink Beach), four in Raja Ampat (snorkel safari, Pianemo viewpoint, Misool homestay). Every transfer is private, every guide is hand-picked. Price band on request.",
    "https://images.unsplash.com/photo-1555400038-63f5ba517a47?auto=format&fit=crop&w=1600&q=80",
    "Across Indonesia Concierge",
    "7 min read",
    "2026-01-15"
  ),
  make(
    "The Honeymoon Package: Bali + Sumba in Ten Slow Days",
    "Tour Packages",
    "Two villas, one private chef, and a horseback sunset on Nihiwatu beach.",
    "Designed with our most romantic clients in mind: five nights in a single-villa Ubud retreat with a private chef and Balinese spa, then five nights at a Sumba beach lodge with horseback rides, private massage decks, and dawn surf lessons. We include a celebrant-led blessing ceremony at a Sumbanese Marapu altar — non-religious, photographically beautiful, deeply moving. Available year-round; we suggest May, June, or September for the best weather across both islands.",
    "https://images.unsplash.com/photo-1537996194471-e657df975ab4?auto=format&fit=crop&w=1600&q=80",
    "Across Indonesia Concierge",
    "6 min read",
    "2026-01-11"
  ),
  make(
    "Family Package: Bali Cultural Discovery for Children Aged 6–14",
    "Tour Packages",
    "Shadow puppets, gamelan lessons, jungle swings and rice-planting — Bali for curious kids and tired parents.",
    "Built around the rhythm of a real family holiday. Seven nights in two locations: a family-villa in Ubud with a daily Balinese cultural workshop for kids (gamelan, shadow puppets, batik), and a beach-club hotel in Sanur for sand, swimming, and easy meals. We add a half-day rice-planting experience that children always remember, and a sea-turtle release on the south coast. All transfers private, all guides bilingual, naptime built in.",
    "https://images.unsplash.com/photo-1554995207-c18c203602cb?auto=format&fit=crop&w=1600&q=80",
    "Across Indonesia Concierge",
    "5 min read",
    "2026-01-07"
  ),
  make(
    "Adventure Package: Volcano, Reef and Jungle in Twelve Days",
    "Tour Packages",
    "Ijen at midnight, Komodo dragons at dawn, orangutans in Tanjung Puting — for the traveler who came to move.",
    "Our most physically demanding itinerary. Day 1–3: Mount Bromo and Ijen with a midnight crater ascent. Day 4–7: a Komodo liveaboard with daily diving. Day 8–12: a klotok riverboat expedition into Tanjung Puting, Borneo, watching wild orangutans from camp. Difficulty is rated moderate — no technical climbing — but you should be comfortable walking three hours a day. We pair every move with a recovery night.",
    "https://images.unsplash.com/photo-1605649461784-edc01e9e0a7d?auto=format&fit=crop&w=1600&q=80",
    "Across Indonesia Concierge",
    "8 min read",
    "2026-01-05"
  ),
  make(
    "Wellness Package: A Seven-Day Reset in the Ubud Foothills",
    "Tour Packages",
    "Sunrise yoga, jamu workshops, a Balinese healer's consultation, and seven mornings of nasi campur.",
    "A genuine rest, not a retreat-resort cliché. We base you in a small Ubud villa with a daily private yoga teacher, a traditional Balinese healer's session, two cooking classes, a jamu (turmeric-tonic) workshop, a rice-paddy long walk, and a single day of full silence. Optional add-on: a night in a meditation hermitage on the slopes of Mount Batukaru. Suited to solo travelers and couples.",
    "https://images.unsplash.com/photo-1532978879514-6cf01a614c25?auto=format&fit=crop&w=1600&q=80",
    "Across Indonesia Wellness",
    "6 min read",
    "2026-01-01"
  ),
  make(
    "The Diver's Trail: A 16-Day Underwater Tour of Eastern Indonesia",
    "Tour Packages",
    "Lembeh muck-diving, Bunaken walls, Raja Ampat mantas — the four-region dive odyssey for advanced certifications.",
    "A bucket-list itinerary for advanced open-water divers. Four nights in Lembeh Strait for the world's strangest macro creatures; four nights in Bunaken National Marine Park for sheer drop walls; eight nights aboard a phinisi schooner through Raja Ampat. Dive masters are senior, oxygen is on every boat, and we plan surface intervals around birding and short jungle walks so non-diving partners are equally happy.",
    "https://images.unsplash.com/photo-1582719471384-894fbb16e074?auto=format&fit=crop&w=1600&q=80",
    "Across Indonesia Dive Team",
    "9 min read",
    "2025-12-26"
  ),
  make(
    "Photographer's Package: Faces, Festivals and Volcanic Light",
    "Tour Packages",
    "A tailored ten-day route for serious photographers — golden hours, portrait sessions, and unposed access.",
    "We design this trip with the photographer's schedule, not the tourist's. Pre-dawn Borobudur from a restricted viewpoint; midday rest; afternoon portrait sessions with weavers, dancers and farmers we have decades-long relationships with; sunset at Tegallalang; night-light on Bromo's caldera; a single private day at a Pasola festival in Sumba (in season). Releases are handled, model fees are paid, and your hard drive will be full by day four.",
    "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=1600&q=80",
    "Across Indonesia Photo Concierge",
    "7 min read",
    "2025-12-20"
  ),
  make(
    "Corporate Retreat Package: Off-Sites for Teams of 8 to 30",
    "Tour Packages",
    "Single-villa estates, private chefs, and an away-from-everything Bali experience for senior teams.",
    "We host an increasing number of European and Australian leadership teams. Our retreat package locks out a 4 to 8-bedroom estate with a dedicated chef, private workshop space, and our own facilitators if needed. Days mix half-day strategy sessions with cultural exposure — a temple ceremony, a traditional rice harvest, a Balinese cooking class — so colleagues come home with shared stories, not just shared slides. Quotes upon brief.",
    "https://images.unsplash.com/photo-1502082553048-f009c37129b9?auto=format&fit=crop&w=1600&q=80",
    "Across Indonesia Corporate",
    "5 min read",
    "2025-12-15"
  ),

  // ──────────────────────── CULTURE (5) ────────────────────────
  make(
    "An Outsider's Guide to a Balinese Temple Ceremony",
    "Culture",
    "Sarongs, sashes, offerings — how to attend the island's most beautiful daily ritual without being the loud guest.",
    "On any given day in Bali, hundreds of temple ceremonies take place. Most are open to respectful visitors. This guide walks you through what a ceremony looks like, what to wear (a sarong and selendang sash — we provide both), where to sit, when to bow, when to receive holy water, and the precise moment it is rude to take a photograph. If you would like to attend with our hosts, our guides are members of the local banjar — they will introduce you to the priest, and you will leave with the offerings the family prepared for you.",
    "https://images.pexels.com/photos/35428411/pexels-photo-35428411.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1600",
    "Putu Adnyana",
    "6 min read",
    "2026-01-10"
  ),
  make(
    "Batik: How to Read Indonesia's Most Layered Textile",
    "Culture",
    "From Solo's royal motifs to Yogya's natural indigos — a primer for the visitor who wants to buy with confidence.",
    "Batik is the most photographed and least understood Indonesian craft. Each region has its own grammar: the geometric parang of central Javanese court batik (once forbidden to commoners), the indigo-and-cream florals of Yogya, the bright north-coast Pekalongan style influenced by Chinese and Arab traders. This guide teaches you how to tell stamped (cap) from hand-drawn (tulis), why a single hand-drawn cloth can take a year to make, and which of our partner ateliers welcome respectful visitors.",
    "https://images.unsplash.com/photo-1605649461784-edc01e9e0a7d?auto=format&fit=crop&w=1600&q=80",
    "Hartati Sutanto",
    "7 min read",
    "2026-01-03"
  ),
  make(
    "Gamelan: A First-Time Listener's Guide to Indonesia's Court Music",
    "Culture",
    "The bronze orchestras of Java and Bali — what to listen for, where to hear them, and how to find a lesson.",
    "Gamelan is the bronze-orchestra music of Java and Bali — and to international ears, the most arresting sound in Southeast Asia. Javanese gamelan is slow, contemplative, and tied to the courts of Solo and Yogyakarta. Balinese gamelan is fast, percussive, and tied to temple festivals. This guide explains the instruments (saron, bonang, gong ageng), the scales (pelog and slendro), and the three locations where we book first-timer sessions: the Yogya kraton, the Ubud village banjar of Padangtegal, and the dance conservatory in Denpasar.",
    "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=1600&q=80",
    "I Wayan Sudirta",
    "6 min read",
    "2025-12-30"
  ),
  make(
    "Wayang Kulit: Watching a Shadow-Puppet Play That Lasts All Night",
    "Culture",
    "How to enjoy a six-hour Javanese shadow-puppet performance — and why the puppeteer is the most respected artist in the land.",
    "Wayang kulit — the shadow-puppet theatre of Java and Bali — is a UNESCO masterpiece, a thousand-year-old form, and an all-night affair. A traditional performance starts at 9 p.m. and ends at sunrise. The dalang (puppeteer) handles every voice, sound effect, comic interlude and philosophical aside. This guide explains the Ramayana storyline, what to bring (a small pillow), and the abridged 90-minute performances we arrange for guests who want the magic without the marathon.",
    "https://images.pexels.com/photos/19758828/pexels-photo-19758828.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1600",
    "Ki Catur Kuncoro",
    "7 min read",
    "2025-12-23"
  ),
  make(
    "Galungan and Kuningan: Travelling in Bali During the Holiest Ten Days",
    "Culture",
    "The penjor bamboo poles, the family pilgrimages, and why this is the most photogenic time to visit Bali.",
    "Galungan marks the victory of dharma over adharma — good over evil — and Kuningan, ten days later, closes the celebration. Tall curved bamboo penjor poles line every street, families dress in their finest white kebaya, and offerings tower above every temple wall. International travelers sometimes worry the island 'shuts down' for the festival; in fact, hotels, restaurants and our drivers all remain open. This is, quietly, the most beautiful time to be on the island. Dates rotate every 210 days — check with our concierge.",
    "https://images.pexels.com/photos/35428411/pexels-photo-35428411.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1600",
    "Ni Luh Sari",
    "5 min read",
    "2025-12-19"
  ),

  // ──────────────────────── FOOD (5) ────────────────────────
  make(
    "Nasi Campur: How to Order Indonesia's Most Honest Meal",
    "Food",
    "A plate of rice and ten small sides — the cheapest and most local way to eat your way across the archipelago.",
    "Nasi campur means 'mixed rice', and every region has its own version. In Bali, expect lawar (a coconut-and-vegetable salad), shredded chicken, sambal matah, peanuts and a soft-boiled egg. In Java, expect tempe, urap, krupuk and gudeg. We teach you how to order it like a local: point at three things behind the warung glass, ask for nasi sedikit (a little rice), and never skip the sambal. Includes our short-list of warungs we send guests to in Ubud, Yogya, Jakarta and Lombok.",
    "https://images.unsplash.com/photo-1455619452474-d2be8b1e70cd?auto=format&fit=crop&w=1600&q=80",
    "Chef Ari Susanto",
    "6 min read",
    "2026-01-14"
  ),
  make(
    "Sambal: A Working Vocabulary of Indonesia's National Condiment",
    "Food",
    "Matah, terasi, ijo, oelek — a beginner's guide to the seven sambals you'll meet on any Indonesian table.",
    "There are over three hundred named sambals in Indonesia. This guide covers the seven you will meet most often: sambal matah (raw, lemongrass-led, the Balinese classic), sambal terasi (with fermented shrimp paste), sambal ijo (green, mild, from Padang), sambal oelek (the brick-red base of every kitchen), sambal kecap (sweet soy and chili), sambal bawang (raw garlic), and sambal kacang (peanut-based, for satay). With each: what it tastes like, what it pairs with, and how spicy on a scale of one to flame.",
    "https://images.unsplash.com/photo-1606491956689-2ea866880c84?auto=format&fit=crop&w=1600&q=80",
    "Chef Ari Susanto",
    "5 min read",
    "2026-01-09"
  ),
  make(
    "Eating in Bali: Beyond the Beach-Club Bowl",
    "Food",
    "From a roadside babi guling in Gianyar to a 14-course private chef's dinner in Sayan — the full Balinese food map.",
    "Bali's restaurant scene has globalised at an astonishing pace, and the wellness-bowl trope can crowd out actual Balinese food. This guide is our antidote: the family-run babi guling (suckling pig) lunch warungs of Gianyar, the bebek bengil (crispy duck) destinations of Ubud, the seafood night-markets of Jimbaran, and the small handful of fine-dining tables that take Indonesian cooking seriously — Locavore, Mosaic, Apéritif, and our friends at Hujan Locale.",
    "https://images.unsplash.com/photo-1604908554027-4f1aceaf91c6?auto=format&fit=crop&w=1600&q=80",
    "Chef Ari Susanto",
    "8 min read",
    "2026-01-06"
  ),
  make(
    "Padang Food: How to Eat Indonesia's Most Famous Cuisine",
    "Food",
    "Beef rendang, gulai, dendeng — and the unique 'no-menu' Padang restaurant ritual every traveler should experience once.",
    "Padang food — from West Sumatra — is Indonesia's most internationally celebrated cuisine. CNN once voted rendang the world's best dish. The Padang restaurant ritual is unique: you sit down, and within ninety seconds the table is covered in fifteen small plates. You eat what you want, pay only for what you touch. This guide explains the dishes (rendang, gulai ayam, dendeng balado, ikan bakar, gulai tunjang), the dining etiquette (eat with your right hand), and where to find the best Padang in Jakarta, Bali and Padang city itself.",
    "https://images.unsplash.com/photo-1565895405229-71bfa9bc4a37?auto=format&fit=crop&w=1600&q=80",
    "Chef Reno Andam",
    "7 min read",
    "2025-12-31"
  ),
  make(
    "Indonesian Coffee: From Sumatra Mandheling to Bali Kintamani",
    "Food",
    "The world's fourth-largest coffee producer — and the four origins every visitor should taste before they leave.",
    "Indonesia produces more coffee than almost anyone, but most international travelers leave without tasting more than the Starbucks airport cup. This guide walks you through the four key origins: Sumatra Mandheling (full-bodied, earthy), Toraja from Sulawesi (bright, balanced), Java (the world's first cultivated coffee), and Bali Kintamani (citrus-led, washed). We include the half-day coffee-farm visit we book in the Munduk highlands and the Ubud roastery our guests always come home talking about.",
    "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=1600&q=80",
    "Chef Ari Susanto",
    "6 min read",
    "2025-12-24"
  ),

  // ──────────────────────── ADVENTURE (4) ────────────────────────
  make(
    "Climbing Mount Rinjani: An Honest Briefing for Lombok's 3,726m Volcano",
    "Adventure",
    "Two days up, one day down, ten thousand vertical metres of pumice — and a crater lake worth every step.",
    "Mount Rinjani is the second-highest volcano in Indonesia and one of the toughest two-day treks in Southeast Asia. The summit push starts at 1:30 a.m. through ankle-deep pumice — three steps up, two steps sliding back. The reward: sunrise over a turquoise crater lake and the entire Lombok-Bali horizon. This honest briefing covers fitness levels needed, gear lists, our porter ethics, and why we recommend a three-day rather than a two-day pace for travelers over 45.",
    "https://images.unsplash.com/photo-1518837695005-2083093ee35b?auto=format&fit=crop&w=1600&q=80",
    "Inaq Putri Trekking Co.",
    "8 min read",
    "2026-01-13"
  ),
  make(
    "Surfing Indonesia: An Intermediate's Guide to Bali, Lombok and Sumba",
    "Adventure",
    "Beyond Padang Padang — the calm-water reef breaks and dry-season swell map for surfers who want progress, not videos.",
    "Indonesia has more world-class waves than any country. This guide is for the intermediate surfer who wants to make real progress: where to stay in Canggu (small, board-friendly hotels we trust), how to time the dry season (April–October), which left-handers on Lombok suit a confident club surfer, and where to find a coach in Sumba for one-on-one mornings. Includes our board-rental partners and a calm-day mention of the spots you should not paddle out at without a guide.",
    "https://images.unsplash.com/photo-1455729552865-3658a5d39692?auto=format&fit=crop&w=1600&q=80",
    "Made Adi",
    "7 min read",
    "2025-12-29"
  ),
  make(
    "Diving Indonesia: The Eight Regions Worth Crossing the World For",
    "Adventure",
    "Lembeh, Bunaken, Komodo, Banda, Raja Ampat, Alor, Wakatobi, Derawan — what each one is best for, and when.",
    "Indonesia sits at the heart of the Coral Triangle and offers more dive variety than any single country on earth. This is our reference map for serious divers: Lembeh for the world's strangest macro creatures, Bunaken for sheer walls, Komodo for current-swept pelagics, Banda for hammerheads, Raja Ampat for pure marine density, Alor for traditional villages plus diving, Wakatobi for resort-style ease, and Derawan in Borneo for whale sharks and stingless jellyfish lakes.",
    "https://images.unsplash.com/photo-1559056199-641a0ac8b55e?auto=format&fit=crop&w=1600&q=80",
    "Captain Reza Pratama",
    "10 min read",
    "2025-12-21"
  ),
  make(
    "Trekking Wae Rebo: The Hidden Mountain Village of West Flores",
    "Adventure",
    "A four-hour hike to seven conical houses, an overnight homestay, and one of the most respectful village experiences in Indonesia.",
    "Wae Rebo is a UNESCO-recognised village of seven conical-roofed houses suspended in a high cloud-forest valley of Flores. There is no road in — only a four-hour walk that ends in a coffee welcome and a night sleeping inside a 1,000-year-old communal house. The village set their own visitor policy long before Indonesian tourism boomed: small groups, payment of a community fee, and a quiet ceremony of welcome. This is one of the most considered cultural experiences in the archipelago. We book it eight weeks ahead.",
    "https://images.unsplash.com/photo-1546484959-f9a381d1330d?auto=format&fit=crop&w=1600&q=80",
    "Boni Wangge",
    "8 min read",
    "2025-12-12"
  ),

  // ──────────────────────── TRAVEL TIPS (4) ────────────────────────
  make(
    "The Indonesia Visa Briefing for International Travelers in 2026",
    "Travel Tips",
    "Visa-on-arrival, e-VOA, the 60-day extension — and exactly what to do at the Bali airport queue.",
    "Most international visitors enter Indonesia on a Visa-on-Arrival (VOA), valid for 30 days and extendable by another 30. The e-VOA, applied for online before departure, saves you 30–60 minutes at the immigration queue in Bali. This guide explains who is eligible (96 nationalities at last count), the fee in IDR and USD, the difference between the social-visit B211 and the tourist B1, and our practical advice on the day of arrival — including the small queue trick most repeat visitors swear by.",
    "https://images.unsplash.com/photo-1521295121783-8a321d551ad2?auto=format&fit=crop&w=1600&q=80",
    "Across Indonesia Concierge",
    "5 min read",
    "2026-01-16"
  ),
  make(
    "When to Visit Indonesia: A Region-by-Region Weather Map",
    "Travel Tips",
    "Bali's two seasons, Komodo's calm-sea window, Raja Ampat's manta months — the calendar planners look at.",
    "Indonesia's weather is more nuanced than a simple 'wet vs. dry'. Bali's dry season runs April–October, but with localised micro-climates. Komodo is best from April to early November. Raja Ampat reverses this — October to April is calmest. Sumba's surf season is May–October. This region-by-region map helps you align your interests (diving, hiking, surfing, culture) with the right months. Always cross-reference with our concierge before booking flights.",
    "https://images.unsplash.com/photo-1573843981267-be1999ff37cd?auto=format&fit=crop&w=1600&q=80",
    "Across Indonesia Concierge",
    "6 min read",
    "2026-01-08"
  ),
  make(
    "Money, Tipping and Bargaining: A Guide for First-Time Visitors",
    "Travel Tips",
    "Rupiah notes, ATM caps, when (and when not) to tip, and the polite art of negotiating without losing the room.",
    "Indonesian rupiah comes in large numbers — a 100,000 note is roughly USD 6 — and it is easy to mis-count zeroes at first. This guide explains the safest ATMs, daily withdrawal caps, when to tip (porters, drivers, guides — yes; restaurants with service charge — no), and the polite Indonesian way to negotiate at a market: a smile, a small joke, and the willingness to walk away. We also cover where credit cards are widely accepted (hotels, fine dining) and where cash is still essential (warungs, drivers, ferry tickets).",
    "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1600&q=80",
    "Across Indonesia Concierge",
    "5 min read",
    "2026-01-03"
  ),
  make(
    "Responsible Travel in Indonesia: A Quiet Code for International Guests",
    "Travel Tips",
    "Dress codes, photography ethics, environmental impact — the small habits that make you a welcome guest.",
    "Indonesia is welcoming, but it is also a country of strong religious and cultural traditions. This is the quiet code we share with every guest before they arrive: cover shoulders and knees at temples, take shoes off before entering homes and certain shops, do not pat children on the head, do not photograph people without asking, do not bring single-use plastics to the smaller islands, and never haggle aggressively over the price of a meal cooked by hand. Travel done quietly is the only travel worth doing.",
    "https://images.unsplash.com/photo-1559494007-9f5847c49d94?auto=format&fit=crop&w=1600&q=80",
    "Across Indonesia Concierge",
    "6 min read",
    "2025-12-27"
  ),
];

export const getBlogPost = (slug) => BLOG_POSTS.find((p) => p.slug === slug);
export const getRelatedPosts = (slug, category, n = 3) =>
  BLOG_POSTS.filter((p) => p.slug !== slug && p.category === category).slice(0, n);
